import { useTranslation } from 'react-i18next';
import { Badge, Card, DataTable, EmptyState } from '@/components/ui';
import {
  useMyAttendance,
  type AttendanceRecord,
} from '@/features/attendance/api';
import { useBranch } from '@/features/branch/branch-context';
import { PageContainer } from '@/layouts/PageContainer';
import styles from './attendance.module.css';

function day(value: string) {
  return new Intl.DateTimeFormat('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
}

function time(value: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-MY', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value));
}

export function AttendancePage() {
  const { t } = useTranslation();
  const attendance = useMyAttendance();
  const { branches } = useBranch();
  const branchName = (id: string) =>
    branches.find((branch) => branch.id === id)?.name ?? id;

  const columns = [
    {
      key: 'date',
      header: t('attendance.date'),
      render: (r: AttendanceRecord) => day(r.date),
    },
    {
      key: 'branch',
      header: t('attendance.branch'),
      render: (r: AttendanceRecord) => branchName(r.branchId),
    },
    {
      key: 'in',
      header: t('attendance.clockIn'),
      render: (r: AttendanceRecord) => time(r.clockedInAt),
    },
    {
      key: 'out',
      header: t('attendance.clockOut'),
      render: (r: AttendanceRecord) => time(r.clockedOutAt),
    },
    {
      key: 'status',
      header: t('attendance.status'),
      render: (r: AttendanceRecord) => (
        <div className={styles.badges}>
          <Badge
            tone={
              r.missingClockOut
                ? 'danger'
                : r.late || r.earlyDeparture
                  ? 'warning'
                  : 'success'
            }
          >
            {t(`attendance.states.${r.state}`)}
          </Badge>
          {r.late && <Badge tone="warning">{t('attendance.late')}</Badge>}
          {r.earlyDeparture && (
            <Badge tone="warning">{t('attendance.early')}</Badge>
          )}
        </div>
      ),
    },
  ];

  return (
    <PageContainer>
      <div className={styles.stack}>
        <Card title={t('attendance.myHistory')} flush>
          <DataTable
            caption={t('attendance.myHistory')}
            rows={attendance.data?.history}
            rowKey={(record) => record.id}
            loading={attendance.isLoading}
            error={attendance.error}
            onRetry={() => void attendance.refetch()}
            empty={<EmptyState title={t('attendance.noHistory')} />}
            columns={columns}
          />
        </Card>
      </div>
    </PageContainer>
  );
}
